import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { clsx } from 'clsx';
import { X } from 'lucide-react';

function focusableIn(container: HTMLElement): HTMLElement[] {
  const nodes = container.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  return Array.from(nodes).filter((el) => !el.hasAttribute('disabled'));
}

/** iOS-style bottom sheet with drag-to-dismiss affordance. */
export function BottomSheet({
  open,
  onClose,
  title,
  children,
  height = 'auto',
  scrollable = true,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  height?: 'auto' | 'half' | 'full';
  /** When false, the body does not scroll as a whole; children manage their own
   *  scrolling (e.g. a chat whose composer stays pinned at the bottom). */
  scrollable?: boolean;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const rootEl = document.getElementById('root');
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = focusableIn(panelRef.current);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const inPanel = panelRef.current.contains(active);
      if (e.shiftKey && (active === first || active === panelRef.current || !inPanel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inPanel)) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    rootEl?.setAttribute('inert', '');

    // Move focus into the sheet once it mounts; restore it on close.
    requestAnimationFrame(() => panelRef.current?.focus());

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      rootEl?.removeAttribute('inert');
      previouslyFocused?.focus();
    };
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50">
          <motion.div
            className="absolute inset-0 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => onCloseRef.current()}
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={typeof title === 'string' ? title : undefined}
            tabIndex={-1}
            className={clsx(
              'sheet-panel absolute inset-x-0 bottom-0 mx-auto flex flex-col overflow-hidden rounded-t-[12px] bg-surface shadow-float outline-none',
              height === 'full' ? 'inset-y-0 rounded-t-none' : 'max-h-[88dvh]',
            )}
            style={{ maxWidth: 560 }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          >
            <div className="flex items-center justify-between px-5 pt-3">
              <div className="w-10" />
              <div className="mx-auto mt-1 h-1.5 w-10 rounded-none bg-ink/15" />
              <button
                onClick={() => onCloseRef.current()}
                className="grid size-8 place-items-center rounded-none bg-surface-2 press"
                aria-label="Tutup"
              >
                <X className="size-4 text-muted" />
              </button>
            </div>
            {title && (
              <div className="px-5 pb-2 pt-1 text-center text-[17px] font-bold text-ink">
                {title}
              </div>
            )}
            <div
              className={clsx(
                'px-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] pt-2',
                scrollable
                  ? 'scrollable overflow-y-auto'
                  : 'flex min-h-0 flex-1 flex-col overflow-hidden',
              )}
            >
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
